import {
  type AppointmentWithDisplay,
  DEFAULT_PERSO_MARGIN_MIN,
  type LocalDate,
  formatDegrees,
  holidayOn,
  persoConflicts,
  weatherEmoji,
} from '@ergo/shared';
import {
  type CSSProperties,
  type FormEvent,
  type MouseEvent,
  type PointerEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api, errorMessage } from '../api';
import { STATUS_LABELS, applySlotChange, firstWordOf, patientHue, titleOf } from '../appointments';
import { Modal } from '../components/Modal';
import { ScopeDialog, type SeriesScope } from '../components/ScopeDialog';
import { useResource } from '../hooks';
import { holidayRangeLabel, holidaysOverDays, useSchoolHolidays } from '../holidays';
import { useMeteoWeek } from '../meteo';
import { clampToDay, conflictLabel, persoOfDay, usePersoEvents } from '../perso';
import {
  addDays,
  atTime,
  dateOf,
  formatDayMonth,
  formatDuration,
  formatWeekRange,
  formatWeekdayShort,
  isValidDate,
  minutesOfTime,
  startOfWeek,
  timeFromMinutes,
  timeOf,
  today,
} from '../time';

const HOUR_HEIGHT = 56;
const PX_PER_MIN = HOUR_HEIGHT / 60;
const SNAP_MIN = 15;
const DEFAULT_FIRST_HOUR = 8;
const DEFAULT_LAST_HOUR = 19;
const DRAG_THRESHOLD_PX = 4;

interface DragState {
  appointment: AppointmentWithDisplay;
  originX: number;
  originY: number;
  startMinutes: number;
  moved: boolean;
  previewStartsAt: string | null;
}

export function Semaine() {
  const [search, setSearch] = useSearchParams();
  const navigate = useNavigate();
  const requested = search.get('date');
  const monday = startOfWeek(requested && isValidDate(requested) ? requested : today());
  const sunday = addDays(monday, 6);

  const appointments = useResource(
    () => api.appointments.list({ from: monday, to: sunday }),
    [monday],
  );
  const perso = usePersoEvents(monday, sunday);
  const holidays = useSchoolHolidays(monday, sunday);
  const meteo = useMeteoWeek(monday);
  const settings = useResource(() => api.settings.get(), []);

  const dragRef = useRef<DragState | null>(null);
  const suppressClickRef = useRef(false);
  const [preview, setPreview] = useState<{ id: number; startsAt: string } | null>(null);
  const [pendingMove, setPendingMove] = useState<{
    appointment: AppointmentWithDisplay;
    startsAt: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [persoNotice, setPersoNotice] = useState<string | null>(null);

  const items = appointments.data ?? [];

  // Monday to Friday; a weekend column only appears when a séance actually lives there.
  const days = useMemo(() => {
    const week = Array.from({ length: 7 }, (_, index) => addDays(monday, index));
    return week.filter(
      (day, index) => index < 5 || items.some((item) => dateOf(item.startsAt) === day),
    );
  }, [monday, items]);
  const [firstHour, lastHour] = useMemo<[number, number]>(() => {
    let first = DEFAULT_FIRST_HOUR;
    let last = DEFAULT_LAST_HOUR;
    for (const item of items) {
      const start = minutesOfTime(timeOf(item.startsAt));
      first = Math.min(first, Math.floor(start / 60));
      last = Math.max(last, Math.ceil((start + item.plannedDurationMin) / 60));
    }
    // Un rendez-vous perso élargit la grille comme une séance : sans quoi un dentiste
    // à 7 h resterait invisible, ce qui est exactement l'inverse du but.
    for (const event of perso) {
      if (event.allDay) continue;
      const { startMin, endMin } = clampToDay(event, dateOf(event.startsAt));
      first = Math.min(first, Math.floor(startMin / 60));
      last = Math.max(last, Math.ceil(endMin / 60));
    }
    return [first, Math.max(last, first + 1)];
  }, [items, perso]);

  const hours = Array.from({ length: lastHour - firstHour }, (_, index) => firstHour + index);
  const gridHeight = (lastHour - firstHour) * HOUR_HEIGHT;

  const byDay = useMemo(() => {
    const grouped = new Map<LocalDate, AppointmentWithDisplay[]>();
    for (const item of items) {
      const startsAt = preview?.id === item.id ? preview.startsAt : item.startsAt;
      const day = dateOf(startsAt);
      const bucket = grouped.get(day);
      if (bucket) bucket.push({ ...item, startsAt });
      else grouped.set(day, [{ ...item, startsAt }]);
    }
    return grouped;
  }, [items, preview]);

  // Le mot passe : il a été lu au moment du geste, il n'a plus à encombrer l'écran.
  useEffect(() => {
    if (persoNotice === null) return;
    const timer = setTimeout(() => setPersoNotice(null), 6000);
    return () => clearTimeout(timer);
  }, [persoNotice]);

  function goTo(date: LocalDate) {
    setSearch(startOfWeek(date) === startOfWeek(today()) ? {} : { date }, { replace: true });
  }

  function openEditor(item: AppointmentWithDisplay) {
    navigate(`/rdv/${item.id}`, { state: { from: `/semaine?date=${monday}` } });
  }

  async function move(appointment: AppointmentWithDisplay, startsAt: string, scope: SeriesScope) {
    setPending(true);
    setError(null);
    try {
      await applySlotChange(appointment, { startsAt }, scope);
      appointments.reload();
      // Le déplacement se fait quand même : elle décide, l'app prévient (DESIGN.md).
      const near = persoConflicts(
        { startsAt, durationMin: appointment.plannedDurationMin },
        perso,
        settings.data?.persoMarginMin ?? DEFAULT_PERSO_MARGIN_MIN,
      );
      setPersoNotice(near[0] ? conflictLabel(near[0]) : null);
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setPending(false);
      setPreview(null);
      setPendingMove(null);
    }
  }

  // The drag listens on `window`, not the séance button: crossing into another day
  // re-parents the button (new DOM node), which would kill a pointer capture mid-drag.
  function onPointerDown(event: PointerEvent<HTMLElement>, item: AppointmentWithDisplay) {
    // Touch keeps its native scrolling: tapping opens the editor instead of dragging.
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    if (dragRef.current) return;
    const drag: DragState = {
      appointment: item,
      originX: event.clientX,
      originY: event.clientY,
      startMinutes: minutesOfTime(timeOf(item.startsAt)),
      moved: false,
      previewStartsAt: null,
    };
    dragRef.current = drag;

    const onMove = (ev: globalThis.PointerEvent) => {
      const dx = ev.clientX - drag.originX;
      const dy = ev.clientY - drag.originY;
      if (!drag.moved && Math.abs(dx) < DRAG_THRESHOLD_PX && Math.abs(dy) < DRAG_THRESHOLD_PX) {
        return;
      }
      drag.moved = true;

      const rawMinutes = drag.startMinutes + dy / PX_PER_MIN;
      const snapped = Math.round(rawMinutes / SNAP_MIN) * SNAP_MIN;
      const minutes = Math.max(
        firstHour * 60,
        Math.min(lastHour * 60 - drag.appointment.plannedDurationMin, snapped),
      );

      const column = document
        .elementFromPoint(ev.clientX, ev.clientY)
        ?.closest<HTMLElement>('[data-date]');
      const date = column?.dataset.date ?? dateOf(drag.appointment.startsAt);

      drag.previewStartsAt = atTime(date, timeFromMinutes(minutes));
      setPreview({ id: drag.appointment.id, startsAt: drag.previewStartsAt });
    };

    const detach = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onCancel);
      dragRef.current = null;
    };

    const onUp = () => {
      detach();
      // A drag that never moved is a plain click: the click handler opens the editor.
      if (!drag.moved) return;
      suppressClickRef.current = true;

      const startsAt = drag.previewStartsAt;
      if (!startsAt || startsAt === drag.appointment.startsAt) {
        setPreview(null);
        return;
      }

      if (drag.appointment.seriesId === null) void move(drag.appointment, startsAt, 'one');
      else setPendingMove({ appointment: drag.appointment, startsAt });
    };

    const onCancel = () => {
      detach();
      setPreview(null);
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onCancel);
  }

  function onEventClick(event: MouseEvent<HTMLElement>, item: AppointmentWithDisplay) {
    event.stopPropagation();
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      return;
    }
    openEditor(item);
  }

  function onEmptyClick(event: MouseEvent<HTMLDivElement>, date: LocalDate) {
    if (dragRef.current || preview) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const minutes = firstHour * 60 + (event.clientY - rect.top) / PX_PER_MIN;
    const snapped = Math.round(minutes / SNAP_MIN) * SNAP_MIN;
    navigate(`/rdv/nouveau?date=${date}&time=${timeFromMinutes(snapped)}`, {
      state: { from: `/semaine?date=${monday}` },
    });
  }

  return (
    <section className="page page--wide">
      <header className="week__header">
        <div className="week__nav">
          <button
            type="button"
            className="button button--icon"
            aria-label="Semaine précédente"
            onClick={() => goTo(addDays(monday, -7))}
          >
            ‹
          </button>
          <h1 className="page__title">{formatWeekRange(monday)}</h1>
          <button
            type="button"
            className="button button--icon"
            aria-label="Semaine suivante"
            onClick={() => goTo(addDays(monday, 7))}
          >
            ›
          </button>
        </div>
        <div className="week__actions">
          <button type="button" className="button button--small" onClick={() => goTo(today())}>
            Cette semaine
          </button>
          <button type="button" className="button button--small" onClick={() => setBulkOpen(true)}>
            Annuler une période…
          </button>
        </div>
      </header>

      {/* Les vacances scolaires de la semaine : un mot, puis les colonnes teintées. */}
      {holidaysOverDays(days, holidays).map((holiday) => (
        <p className="vacances-banner" key={holiday.start}>
          {holiday.name} · {holidayRangeLabel(holiday)}
        </p>
      ))}

      {error && <p className="error" role="alert">{error}</p>}
      {appointments.error && <p className="error" role="alert">{appointments.error}</p>}
      {persoNotice && (
        <p className="note-sun note-sun--toast" role="status">
          {persoNotice}
        </p>
      )}

      <div className="week">
        <div className="week__inner" style={{ '--day-count': days.length } as CSSProperties}>
          <div className="week__row week__row--head">
            <div className="week__gutter" />
            {days.map((day) => (
              <div
                key={day}
                className={`week__dayhead${holidayOn(day, holidays) ? ' week__dayhead--vacances' : ''}${day === today() ? ' is-today' : ''}`}
              >
                <span className="week__dayname">{formatWeekdayShort(day)}</span>
                <span className="week__daynum">
                  {Number(day.slice(8, 10))}
                  {/* Sur la même ligne que le quantième : l'en-tête ne grandit pas,
                      et les jours hors prévision n'affichent simplement rien. */}
                  <DayMeteo day={day} meteo={meteo} />
                </span>
                {/* Une journée entière n'a pas d'heure : elle coiffe la colonne. */}
                {persoOfDay(perso, day).allDay.map((event) => (
                  <span className="week__perso-allday" key={event.uid} title={event.title}>
                    {event.title}
                  </span>
                ))}
              </div>
            ))}
          </div>

          <div className="week__row week__body" style={{ height: gridHeight }}>
            <div className="week__gutter">
              {hours.map((hour) => (
                <span key={hour} className="week__hour" style={{ top: (hour - firstHour) * HOUR_HEIGHT }}>
                  {String(hour).padStart(2, '0')}:00
                </span>
              ))}
            </div>

            {days.map((day) => (
              <div
                key={day}
                className={`week__day${holidayOn(day, holidays) ? ' week__day--vacances' : ''}${day === today() ? ' is-today' : ''}`}
                data-date={day}
                onClick={(event) => onEmptyClick(event, day)}
              >
                {hours.map((hour) => (
                  <div
                    key={hour}
                    className="week__slot"
                    style={{ top: (hour - firstHour) * HOUR_HEIGHT, height: HOUR_HEIGHT }}
                  />
                ))}

                {/* Derrière les séances, et sans réagir au clic : c'est un décor,
                    posé là pour qu'elle voie ce que sa journée a déjà de pris. */}
                {persoOfDay(perso, day).timed.map((event) => {
                  const { startMin, endMin } = clampToDay(event, day);
                  return (
                    <div
                      key={event.uid}
                      className="week__perso"
                      aria-hidden="true"
                      style={{
                        top: (startMin - firstHour * 60) * PX_PER_MIN,
                        height: Math.max(14, (endMin - startMin) * PX_PER_MIN - 2),
                      }}
                    >
                      <span className="week__perso-title">{event.title}</span>
                    </div>
                  );
                })}

                {layoutDay(byDay.get(day) ?? []).map(({ item, column, columns }) => {
                  const start = minutesOfTime(timeOf(item.startsAt));
                  const classes = ['week__event'];
                  if (item.status === 'cancelled' || item.status === 'no_show') classes.push('is-off');
                  if (item.important) classes.push('is-important');
                  if (preview?.id === item.id) classes.push('is-dragging');

                  return (
                    <button
                      key={item.id}
                      type="button"
                      className={classes.join(' ')}
                      disabled={pending}
                      style={
                        {
                          '--hue': patientHue(item.patientId),
                          top: (start - firstHour * 60) * PX_PER_MIN,
                          height: Math.max(18, item.plannedDurationMin * PX_PER_MIN - 2),
                          left: `${(column / columns) * 100}%`,
                          width: `${(1 / columns) * 100}%`,
                        } as CSSProperties
                      }
                      onClick={(event) => onEventClick(event, item)}
                      onPointerDown={(event) => onPointerDown(event, item)}
                    >
                      <span className="week__event-head">
                        <span className="week__event-time">{timeOf(item.startsAt)}</span>
                        <span className="week__event-name">
                          {titleOf({ ...item, patientName: firstWordOf(item.patientName) })}
                        </span>
                      </span>
                      {item.plannedDurationMin >= 40 && (
                        <span className="week__event-place">{item.locationLabel}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>

      <p className="hint week__legend">
        Cliquez un créneau vide pour créer une séance, une séance pour la modifier. À la souris, les
        séances se déplacent par glisser-déposer (pas de 15 min).
      </p>

      {pendingMove && (
        <ScopeDialog
          title="Déplacer la séance"
          description={`${pendingMove.appointment.patientName} — ${formatDayMonth(dateOf(pendingMove.startsAt))} à ${timeOf(pendingMove.startsAt)} (${formatDuration(pendingMove.appointment.plannedDurationMin)}). Cette séance fait partie d’une série.`}
          oneLabel="Seulement cette séance"
          futureLabel="Toutes les séances à venir"
          pending={pending}
          onChoose={(scope) => void move(pendingMove.appointment, pendingMove.startsAt, scope)}
          onCancel={() => {
            setPendingMove(null);
            setPreview(null);
          }}
        />
      )}

      {bulkOpen && (
        <BulkCancelDialog
          from={monday}
          to={sunday}
          onClose={() => setBulkOpen(false)}
          onDone={() => appointments.reload()}
        />
      )}
    </section>
  );
}

/** Le temps qu'il fera ce jour-là, en petit, sous la colonne : « ☀️ 24° ». */
function DayMeteo({ day, meteo }: { day: LocalDate; meteo: ReturnType<typeof useMeteoWeek> }) {
  const daily = meteo.get(day);
  const emoji = daily ? weatherEmoji(daily.code) : null;
  if (!daily || emoji === null) return null;

  return (
    <span className="week__meteo">
      {emoji} {formatDegrees(daily.tempMaxC)}
    </span>
  );
}

interface Positioned {
  item: AppointmentWithDisplay;
  column: number;
  columns: number;
}

/** Overlapping séances share the width of the day column, side by side. */
function layoutDay(items: AppointmentWithDisplay[]): Positioned[] {
  const sorted = [...items].sort((a, b) => a.startsAt.localeCompare(b.startsAt));
  const placed: Positioned[] = [];
  let group: Positioned[] = [];
  let groupEnd = -1;

  const flush = () => {
    const columns = group.reduce((max, entry) => Math.max(max, entry.column + 1), 1);
    for (const entry of group) placed.push({ ...entry, columns });
    group = [];
    groupEnd = -1;
  };

  for (const item of sorted) {
    const start = minutesOfTime(timeOf(item.startsAt));
    const end = start + item.plannedDurationMin;
    if (start >= groupEnd && group.length > 0) flush();

    const taken = new Set(
      group
        .filter((entry) => minutesOfTime(timeOf(entry.item.startsAt)) + entry.item.plannedDurationMin > start)
        .map((entry) => entry.column),
    );
    let column = 0;
    while (taken.has(column)) column += 1;

    group.push({ item, column, columns: 1 });
    groupEnd = Math.max(groupEnd, end);
  }
  if (group.length > 0) flush();

  return placed;
}

interface BulkCancelDialogProps {
  from: LocalDate;
  to: LocalDate;
  onClose: () => void;
  onDone: () => void;
}

function BulkCancelDialog({ from, to, onClose, onDone }: BulkCancelDialogProps) {
  const [start, setStart] = useState(from);
  const [end, setEnd] = useState(to);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [cancelled, setCancelled] = useState<number | null>(null);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setPending(true);
    setError(null);
    try {
      const result = await api.appointments.bulkCancel({ from: start, to: end });
      setCancelled(result.cancelled);
      onDone();
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setPending(false);
    }
  }

  return (
    <Modal title="Annuler une période" onClose={onClose}>
      {cancelled === null ? (
        <form className="form" onSubmit={onSubmit}>
          <p className="modal__text">
            Maladie, vacances : toutes les séances encore prévues sur la période passent en «{' '}
            {STATUS_LABELS.cancelled} ».
          </p>
          <div className="field-row">
            <label className="field">
              <span className="field__label">Du</span>
              <input
                className="input"
                type="date"
                value={start}
                onChange={(event) => setStart(event.target.value)}
                required
              />
            </label>
            <label className="field">
              <span className="field__label">Au</span>
              <input
                className="input"
                type="date"
                value={end}
                min={start}
                onChange={(event) => setEnd(event.target.value)}
                required
              />
            </label>
          </div>
          {error && <p className="error" role="alert">{error}</p>}
          <div className="form__actions">
            <button className="button button--danger" type="submit" disabled={pending}>
              {pending ? 'Annulation…' : 'Annuler ces séances'}
            </button>
            <button type="button" className="button button--ghost" onClick={onClose}>
              Retour
            </button>
          </div>
        </form>
      ) : (
        <>
          <p className="modal__text">
            {cancelled === 0
              ? 'Aucune séance à annuler sur cette période.'
              : `${cancelled} séance${cancelled > 1 ? 's' : ''} annulée${cancelled > 1 ? 's' : ''}.`}
          </p>
          <div className="modal__actions">
            <button type="button" className="button button--primary" onClick={onClose}>
              Fermer
            </button>
          </div>
        </>
      )}
    </Modal>
  );
}
